import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { Box, IconButton, Link, Tooltip } from '@mui/material';
import { RhfSelect } from '@core/components';
import { useDebouncedValue } from '@core/hooks/useDebouncedValue';
import { useInfinitePage } from '@core/hooks/useInfinitePage';
import {
  type Design,
  type DesignQuery,
  designKeys,
  getDesignsPage,
} from '../../../api/design';
import DesignDialog from '../../components/DesignDialog';
import { VestmentType } from '../../enum/vestmentType';
import { getAlbFeatures } from '../../utils/alb';
import { getChasubleFeatures } from '../../utils/chasuble';
import { getImageThumbnailUrl } from '../../utils/image';
import type { BaseLineItemFormValues } from '../../utils/orderItem';
import { getStoleFeatures } from '../../utils/stole';
import { isDesignVestmentType } from '../../utils/vestment';
import { DesignBrowseDialog } from './DesignBrowseDialog';

const PICKER_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

interface RhfDesignPickerProps {
  vestmentType?: VestmentType;
  disabled?: boolean;
  onValueChange?: (designCode: string) => void;
}

/** The design types a new design for `vestmentType` can have. */
function getUploadTypes(vestmentType?: VestmentType): VestmentType[] {
  if (!vestmentType) {
    return [];
  }
  const designTypes = getChasubleFeatures(vestmentType)?.designTypes ??
    getStoleFeatures(vestmentType)?.designTypes ??
    getAlbFeatures(vestmentType)?.designTypes ?? [vestmentType];
  return designTypes.filter(isDesignVestmentType);
}

function isNotInCatalog(design: Design | null, code: string): boolean {
  return !!code && (!design || !!design.isHidden || design.code !== code);
}

/**
 * Free-typed codes are kept, and `design` follows the code: the matching
 * design when there is one, otherwise null.
 */
export function RhfDesignPicker({
  vestmentType,
  disabled,
  onValueChange,
}: RhfDesignPickerProps) {
  const queryClient = useQueryClient();
  const { control, getValues, setValue } =
    useFormContext<BaseLineItemFormValues>();
  const [design, designCode] = useWatch({
    control,
    name: ['design', 'designCode'],
  });
  const [query, setQuery] = useState('');
  const [uploadCode, setUploadCode] = useState<string | null>(null);
  const [browseOpen, setBrowseOpen] = useState(false);
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);

  const designQuery: DesignQuery = {
    vestmentType,
    search: search || undefined,
  };
  const {
    items: designs,
    isLoading,
    isFetching,
    loadMore: loadMoreDesigns,
  } = useInfinitePage({
    queryKey: designKeys.picker(designQuery),
    query: designQuery,
    pageSize: PICKER_PAGE_SIZE,
    fetchPage: getDesignsPage,
    enabled: !!vestmentType,
    keepPrevious: (previous) => previous?.vestmentType === vestmentType,
  });
  const uploadTypes = useMemo(
    () => getUploadTypes(vestmentType),
    [vestmentType]
  );

  const syncDesign = useCallback(
    (value: string) => {
      const code = value.trim();
      const current = getValues('design');
      if (current?.code === code) {
        return;
      }
      const match = designs.find((option) => option.code === code) ?? null;
      if (match !== current) {
        setValue('design', match, { shouldDirty: true });
      }
    },
    [designs, getValues, setValue]
  );

  useEffect(() => {
    syncDesign(getValues('designCode'));
  }, [syncDesign, getValues]);

  const code = designCode.trim();
  const canUpload = uploadTypes.length > 0;
  const isDefaultCode =
    code === getChasubleFeatures(vestmentType)?.defaultDesignCode;
  const notInCatalog = !isDefaultCode && isNotInCatalog(design, code);

  const selectDesign = (selected: Design) => {
    setValue('design', selected, { shouldDirty: true });
    setValue('designCode', selected.code, { shouldDirty: true });
    onValueChange?.(selected.code);
    setBrowseOpen(false);
  };

  const onUploaded = (uploaded: Design) => {
    selectDesign(uploaded);
    void queryClient.invalidateQueries({ queryKey: designKeys.all });
  };

  const keepTypedCode = (typedCode: string) => {
    setValue('designCode', typedCode, { shouldDirty: true });
    syncDesign(typedCode);
    onValueChange?.(typedCode);
    setBrowseOpen(false);
  };

  const openUpload = (prefill = '') =>
    setUploadCode(prefill || (notInCatalog ? code : ''));

  const searchSettled = !isFetching && search === query.trim();
  const helperText =
    searchSettled && notInCatalog ? (
      <NotInCatalogText
        onUpload={canUpload ? () => openUpload() : undefined}
        disabled={disabled}
      />
    ) : undefined;

  return (
    <>
      <RhfSelect<BaseLineItemFormValues, Design>
        name="designCode"
        label="Design"
        items={designs}
        getOptionValue={(design) => design.code}
        getOptionLabel={(design) => design.code}
        renderOption={({ item, label }) => (
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <DesignOptionThumbnail design={item} />
            {label}
          </Box>
        )}
        helperText={helperText}
        endAdornment={
          <Tooltip title="Browse designs">
            <span>
              <IconButton
                size="small"
                aria-label="Browse designs"
                disabled={disabled || !vestmentType}
                onClick={() => setBrowseOpen(true)}
                sx={{ p: 0.5, mr: '-2px' }}
              >
                <PhotoLibraryOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
        freeSolo
        loading={isLoading}
        disabled={disabled}
        onQueryChange={setQuery}
        onListboxScrollEnd={loadMoreDesigns}
        onValueChange={(value) => {
          syncDesign(value);
          onValueChange?.(value);
        }}
      />
      {browseOpen && vestmentType ? (
        <DesignBrowseDialog
          onClose={() => setBrowseOpen(false)}
          vestmentType={vestmentType}
          initialSearch={query.trim()}
          selectedCode={code}
          onSelect={selectDesign}
          onUpload={canUpload ? openUpload : undefined}
          onUseCode={keepTypedCode}
        />
      ) : null}
      {uploadCode !== null ? (
        <DesignDialog
          onClose={() => setUploadCode(null)}
          onSuccess={onUploaded}
          inputValue={uploadCode}
          vestmentTypes={uploadTypes}
        />
      ) : null}
    </>
  );
}

interface NotInCatalogTextProps {
  onUpload?: () => void; // Omitted when designs can't be uploaded for the vestment type.
  disabled?: boolean;
}

function NotInCatalogText({ onUpload, disabled }: NotInCatalogTextProps) {
  return (
    <>
      Not in catalog
      {onUpload ? (
        <>
          {' – '}
          <Link
            component="button"
            type="button"
            variant="inherit"
            disabled={disabled}
            onClick={onUpload}
            sx={{ verticalAlign: 'baseline' }}
          >
            Upload image
          </Link>
        </>
      ) : null}
    </>
  );
}

const THUMBNAIL_SX = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: 0.5,
  bgcolor: 'action.hover',
};

function DesignOptionThumbnail({ design }: { design: Design }) {
  const src = getImageThumbnailUrl(design);
  return src ? (
    <Box
      component="img"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      sx={{ ...THUMBNAIL_SX, objectFit: 'cover' }}
    />
  ) : (
    <Box component="span" sx={THUMBNAIL_SX} />
  );
}
